'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import React from 'react';
import CountryCard from './CountryCard';
import { options } from '@/components/ui/languageSwitcher';
import { cn } from '@/lib/utils';

type RegionSelectModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: typeof options;
  onSelect: (code: string) => void;
  title?: string;
  description?: string;
  /** Render above the US pre-launch screen (z-[999]). Used by geo-routing popup. */
  elevated?: boolean;
};

export default function RegionSelectModal({
  open,
  onOpenChange,
  options,
  onSelect,
  title,
  description,
  elevated = false,
}: RegionSelectModalProps) {
  function handleSelect(code: string) {
    onSelect(code);
    onOpenChange(false);
  }

  return (
    <article>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
          overlayClass={cn(
            'backdrop-blur-2xl bg-[#0A0A0A]/70 animate-in-overlay',
            elevated && 'z-[1000]'
          )}
          className={cn(
            'max-h-[90dvh] bg-white overflow-hidden w-[95%] max-w-[520px] border-[#EEEEEE] gap-0 animate-in-slow',
            elevated && 'z-[1000]'
          )}
        >
          <DialogHeader className="pb-5 text-center mx-auto">
            <DialogTitle
              className={`text-lg md:text-[20px] mr-0 mt-1 md:mt-0 leading-[1.3]`}
            >
              {title || 'Choose Your Region'}
            </DialogTitle>
            <DialogDescription
              className={cn(
                'text-base md:text-lg font-proxima_font text-[#303030] max-w-[360px]',
                description && 'max-w-sm'
              )}
            >
              {description ||
                'Select your region to view local pricing, currency and delivery options.'}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[80dvh] h-full overflow-y-auto rounded-lg border border-[#EEEEEE] divide-y divide-[#EEEEEE]">
            {options.map((region) => (
              <CountryCard
                key={region.code}
                flagUrl={region.flag}
                countryName={region.country}
                language="English"
                currencyLabel={region.currency}
                onClick={() => handleSelect(region.code)}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </article>
  );
}
